import { Box, Typography, Stack, Grid, Avatar, useTheme } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PetsIcon from '@mui/icons-material/Pets';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AddIcon from '@mui/icons-material/Add';
import { Badge } from '../../../../shared/components/ui/Badge';
import { Card } from '../../../../shared/components/ui/Card';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { User } from '../../context/UserContext';
import { getAttributeValue } from '../../../../utils/getAttributeLabelAndValue';
import { Button } from '../../../../shared/components/ui/Button';
import { Modal } from '../../../../shared/components/ui/Modal';
import { useState } from 'react';


interface UserInfoProps {
  user: User;
}


const formatDate = (dateString: string): string => {
  try {
    return format(new Date(dateString), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR });
  } catch {
    return 'Data inválida';
  }
};

export function UserInfo({ user }: UserInfoProps) {
  const theme = useTheme();
  const [openPetModal, setOpenPetModal] = useState(false);
  const [openVehicleModal, setOpenVehicleModal] = useState(false);

  return (
    <>
      {/* Avatar e Informações Básicas */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, mb: 4 }}>
        <Box sx={{ position: 'relative', width: 110, height: 110 }}>
          <Avatar 
            sx={{ width: 110, height: 110, fontSize: 48, bgcolor: 'primary.main' }}
          >
            {user.name?.[0]?.toUpperCase()}
          </Avatar>
        </Box>
        <Box flex={1}>
          <Typography sx={{ color: theme.palette.text.primary, fontWeight: 800, fontSize: 28, letterSpacing: -1 }}>
            {user.name}
          </Typography>
          <Typography sx={{ color: theme.palette.text.secondary, fontSize: 16, fontWeight: 500 }}>
            {user.role}
          </Typography>
          <Stack direction="row" spacing={2} mt={1} alignItems="center">
            {user.email && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <EmailIcon sx={{ color: theme.palette.text.secondary, fontSize: 16 }} />
                <Typography sx={{ color: theme.palette.text.secondary, fontSize: 14 }}>{user.email}</Typography>
              </Box>
            )}
            {user.phone && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PhoneIcon sx={{ color: theme.palette.text.secondary, fontSize: 16 }} />
                <Typography sx={{ color: theme.palette.text.secondary, fontSize: 14 }}>{user.phone}</Typography>
              </Box>
            )}
          </Stack>
        </Box>
      </Box>

      {/* Detalhes */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ background: theme.palette.background.paper, borderRadius: 3, p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PersonIcon sx={{ color: theme.palette.primary.main, fontSize: 22, mr: 1 }} />
              <Typography fontWeight={700} fontSize={17} color={theme.palette.text.primary}>Dados do Usuário</Typography>
            </Box>
            <Box component="dl" sx={{ m: 0 }}>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: theme.palette.text.secondary, fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 15 }}>Nome:</dt>
                <dd style={{ margin: 0, marginLeft: 16, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontWeight: 500, fontSize: 15 }}>{user.name}</dd>
              </Box>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: theme.palette.text.secondary, fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 15 }}>E-mail:</dt>
                <dd style={{ margin: 0, marginLeft: 16, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontWeight: 500, fontSize: 15 }}>{user.email}</dd>
              </Box>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: theme.palette.text.secondary, fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 15 }}>Telefone:</dt>
                <dd style={{ margin: 0, marginLeft: 16, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontWeight: 500, fontSize: 15 }}>{user.phone}</dd>
              </Box>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: theme.palette.text.secondary, fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 15 }}>CPF/RG:</dt>
                <dd style={{ margin: 0, marginLeft: 16, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontWeight: 500, fontSize: 15 }}>{user.cpf_or_rg}</dd>
              </Box>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: theme.palette.text.secondary, fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 15 }}>Nascimento:</dt>
                <dd style={{ margin: 0, marginLeft: 16, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontWeight: 500, fontSize: 15 }}>{user.birth_date}</dd>
              </Box>
            </Box>
          </Card>
        </Grid>

        {/* Card de Datas */}
        <Grid item xs={12}>
          <Card sx={{ background: theme.palette.background.paper, borderRadius: 3, p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CalendarTodayIcon sx={{ color: theme.palette.primary.main, fontSize: 22, mr: 1 }} />
              <Typography fontWeight={700} fontSize={17} color={theme.palette.text.primary}>Histórico</Typography>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box component="dl" sx={{ m: 0 }}>
                  <Box sx={{ display: 'flex', mb: 1, alignItems: 'flex-start' }}>
                    <dt style={{ width: 120, color: theme.palette.text.secondary, fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 15 }}>Criado em:</dt>
                    <dd style={{ margin: 0, marginLeft: 16, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontWeight: 500, fontSize: 15 }}>
                      {user.created_at ? formatDate(user.created_at) : '-'}
                    </dd>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Grid>
      </Grid>
    </>
  );
}